// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import { createTheme } from '@mui/material/styles';

/**
 * Charte Vigeos appliquée au dashboard de l'équipe.
 *
 * Même charte que le site public, la console de contrats (`public/contrats.html`)
 * et la console de gestion (`apps/gestion/index.html`) : fond bleu nuit en
 * dégradé, cartes de verre, Lexend pour les titres, Source Sans 3 pour le texte.
 * La console de contrats s'ouvre depuis le dashboard, dans le même onglet.
 *
 * Le maximum de la charte est posé ici, au niveau du thème, pour que les pages
 * n'aient pas à reprendre leurs couleurs une à une. Restent aux pages les
 * couleurs qui portent un sens dans un `sx` (ligne d'alerte critique, panneau
 * creusé du détail technique d'un logement).
 */

// Jetons de la charte : mêmes valeurs et mêmes noms que les variables CSS de
// contrats.html et de apps/gestion/index.html, à faire évoluer ensemble. Les
// composants lisent les couleurs dans la palette (`text.primary`,
// `secondary.main`, `background.paper`...) ; le verre et sa bordure, qui n'y ont
// pas de place, sont exportés.
const PRIMARY = '#2563EB';
const PRIMARY_LIGHT = '#3B82F6';
const PRIMARY_DARK = '#1E40AF';
const SKY = '#7EB3FF';

const DEEP_TOP = '#080F1F';
const DEEP_MID = '#0C1A33';
const DEEP_BOTTOM = '#12264A';

const INK = '#F4F7FC';
const SLATE = '#B9C6DC';
const FAINT = '#8296B5';

export const GLASS = 'rgba(59,130,246,.10)';
export const GLASS_BORDER = 'rgba(96,165,250,.32)';
const PANEL = '#0B1730';
const PANEL_LINE = 'rgba(126,179,255,.12)';

const DANGER = '#F87171';
const WARNING = '#FBBF24';
const SUCCESS = '#34D399';
const INFO = '#60A5FA';

// Bordure des champs et filet des lignes de tableau : deux valeurs distinctes
// de la charte, plus discrètes que la bordure de verre.
const FIELD_BG = 'rgba(59,130,246,.12)';
const FIELD_BORDER = 'rgba(126,179,255,.26)';
const ROW_LINE = 'rgba(126,179,255,.10)';
const HOVER = 'rgba(59,130,246,.07)';

const BACKGROUND = `linear-gradient(160deg, ${DEEP_TOP}, ${DEEP_MID} 45%, ${DEEP_BOTTOM})`;
const GLASS_SHADOW = '0 10px 30px rgba(37,99,235,.25)';
const BUTTON_SHADOW = '0 8px 18px rgba(37,99,235,.4)';

const HEAD = "'Lexend', system-ui, -apple-system, sans-serif";
const BODY = "'Source Sans 3', system-ui, -apple-system, sans-serif";

// Fond teinté d'une pastille ou d'un encart d'état, recomposé à partir du
// triplet RVB de chaque couleur d'état.
const TINTS = {
  success: { rgb: '52,211,153', couleur: SUCCESS },
  warning: { rgb: '251,191,36', couleur: WARNING },
  error: { rgb: '248,113,113', couleur: DANGER },
  info: { rgb: '96,165,250', couleur: INFO },
};
const tint = (label, opacite) => `rgba(${TINTS[label].rgb},${opacite})`;

// Encart d'état (Alert) : bordure à 50 %, fond à 10 %, texte et icône à la
// couleur pleine, comme `.alertEntry` dans contrats.html.
const notice = (label) => ({
  color: TINTS[label].couleur,
  backgroundColor: tint(label, '.10'),
  border: `1px solid ${tint(label, '.5')}`,
  '& .MuiAlert-icon': { color: TINTS[label].couleur },
});

// Pastille pleine (Chip) : fond à 14 %, texte à la couleur pleine, jamais
// d'aplat vif, qui éblouirait sur ce fond, surtout sur un écran consulté de nuit.
const pastille = (label) => ({
  color: TINTS[label].couleur,
  backgroundColor: tint(label, '.14'),
  '& .MuiChip-deleteIcon': { color: TINTS[label].couleur, opacity: 0.7 },
});

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: PRIMARY, light: PRIMARY_LIGHT, dark: PRIMARY_DARK, contrastText: '#fff' },
    secondary: { main: SKY, light: '#A8CDFF', dark: '#4A87DB' },
    // Les états portent les variantes claires de la charte : les variantes
    // sombres disparaissent sur ce fond. Elles restent en `dark`, que MUI
    // n'utilise que pour des survols et des bordures.
    success: { main: SUCCESS, light: '#6EE7B7', dark: '#0F9D74' },
    warning: { main: WARNING, light: '#FCD34D', dark: '#B77503' },
    error: { main: DANGER, light: '#FCA5A5', dark: '#B91C1C' },
    info: { main: INFO, light: '#93C5FD', dark: PRIMARY_DARK },
    text: { primary: INK, secondary: SLATE, disabled: FAINT },
    // `background.default` est la couleur plate de repli : elle sert avant que
    // le dégradé soit peint et derrière les surfaces qui n'en veulent pas.
    // Le dégradé lui-même est posé plus bas, dans MuiCssBaseline.
    background: { default: DEEP_MID, paper: PANEL },
    divider: PANEL_LINE,
    action: {
      hover: HOVER,
      selected: 'rgba(59,130,246,.14)',
      // 55 % et non les 35 % de MUI : sur le fond de la charte, 35 % donne
      // 2,3:1, sous le seuil de 3:1 d'un élément graphique (flèche d'une liste
      // déroulante désactivée, icône d'étape « à faire »). 55 % donne 3,8:1,
      // en retrait du texte plein sans être invisible.
      disabled: 'rgba(185,198,220,.55)',
      disabledBackground: 'rgba(126,179,255,.12)',
    },
  },
  typography: {
    fontFamily: BODY,
    h1: { fontFamily: HEAD, fontWeight: 700 },
    h2: { fontFamily: HEAD, fontWeight: 700 },
    h3: { fontFamily: HEAD, fontWeight: 700 },
    h4: { fontFamily: HEAD, fontWeight: 700 },
    h5: { fontFamily: HEAD, fontWeight: 600 },
    h6: { fontFamily: HEAD, fontWeight: 600 },
    subtitle1: { fontFamily: HEAD, fontWeight: 600 },
    subtitle2: { fontFamily: HEAD, fontWeight: 600 },
    overline: { fontFamily: HEAD, fontWeight: 600, letterSpacing: 1, color: SKY },
    button: { fontFamily: HEAD, fontWeight: 600, textTransform: 'none' },
  },
  // Rayon par défaut : 14 px, celui des boutons de la charte. Les cartes
  // montent à 22, les champs descendent à 12, les pastilles vont à 999.
  shape: { borderRadius: 14 },

  components: {
    // Fond de page. CssBaseline pose d'abord `background-color` (couleur
    // plate) ; ces surcharges, émises après dans la même feuille, l'écrasent
    // par le dégradé via la propriété raccourcie `background`. C'est le seul
    // endroit où le fond du dashboard est écrit.
    //
    // `background-attachment: fixed` fixe le dégradé au cadre de vue : il ne
    // défile ni ne se répète. `min-height` sur html et #root le prolonge sous
    // une page plus courte que l'écran.
    MuiCssBaseline: {
      styleOverrides: {
        ':root': { colorScheme: 'dark' },
        html: { minHeight: '100%' },
        body: {
          minHeight: '100vh',
          background: BACKGROUND,
          backgroundAttachment: 'fixed',
          backgroundColor: DEEP_MID,
          color: INK,
          WebkitFontSmoothing: 'antialiased',
        },
        '#root': { minHeight: '100vh' },
        // Les liens bruts des pages (un mailto sur la page de paiement, par
        // exemple) ne passent pas par MUI et resteraient au bleu du navigateur,
        // illisible sur ce fond.
        a: { color: SKY },
        'code, pre': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
        // Les titres HTML bruts (un h1 posé à la main) ne passent pas par MUI :
        // ils reçoivent au moins la police des titres.
        'h1, h2, h3': { fontFamily: HEAD },
      },
    },

    // `backgroundImage: 'none'` n'est pas cosmétique : en mode sombre, MUI
    // superpose à chaque Paper un voile blanc proportionnel à son élévation
    // (`--Paper-overlay`), qui virerait les cartes au gris laiteux.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: GLASS,
          border: `1px solid ${GLASS_BORDER}`,
          borderRadius: 22,
          backdropFilter: 'blur(14px)',
          boxShadow: GLASS_SHADOW,
          color: INK,
        },
        rounded: { borderRadius: 22 },
        // Variante creusée et opaque, pas du verre : elle sert d'encadré dans
        // une fenêtre modale déjà translucide (identifiant et mot de passe
        // provisoire d'un compte de direction, montrés une seule fois), où deux
        // couches de verre ne se liraient plus. Plus sombre que la fenêtre, elle
        // s'en détache comme l'aperçu de document de la console de contrats.
        // Sans flou, elle évite aussi une passe de rendu par encadré, et
        // l'écran de facturation en compte des dizaines.
        outlined: {
          backgroundColor: DEEP_TOP,
          border: `1px solid ${GLASS_BORDER}`,
          borderRadius: 16,
          backdropFilter: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiCard: { styleOverrides: { root: { backgroundImage: 'none', borderRadius: 22 } } },
    MuiCardContent: { styleOverrides: { root: { '&:last-child': { paddingBottom: 20 } } } },

    // La barre d'application reprend l'en-tête des consoles : du verre à bord
    // droit, souligné d'un filet, sans ombre. Un bandeau bleu plein poserait un
    // aplat vif en haut d'un écran qui n'en a aucun autre.
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: GLASS,
          backdropFilter: 'blur(14px)',
          borderRadius: 0,
          border: 'none',
          borderBottom: `1px solid ${GLASS_BORDER}`,
          boxShadow: 'none',
          color: INK,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // Un bouton désactivé s'efface par l'opacité, comme dans la charte
        // (`.btn:disabled{opacity:.45}`) : le gris de MUI est presque invisible
        // sur ce fond. `color` est indispensable : sans lui, l'opacité s'ajoute
        // au gris de MUI au lieu de le remplacer (1,4:1 mesuré sur le fond de
        // la charte). Avec la couleur pleine, le libellé est à 4:1.
        root: {
          borderRadius: 14,
          fontFamily: HEAD,
          fontWeight: 600,
          textTransform: 'none',
          '&.Mui-disabled': { opacity: 0.45, color: INK },
        },
        contained: { boxShadow: BUTTON_SHADOW, '&:hover': { boxShadow: BUTTON_SHADOW } },
        containedPrimary: {
          backgroundImage: `linear-gradient(135deg, ${PRIMARY_LIGHT}, ${PRIMARY_DARK})`,
          color: '#fff',
        },
        containedError: { backgroundImage: 'linear-gradient(135deg, #EF4444, #991B1B)', color: '#fff' },
        containedSuccess: { backgroundImage: 'linear-gradient(135deg, #34D399, #047857)', color: '#04160F' },
        containedWarning: { backgroundImage: 'linear-gradient(135deg, #FBBF24, #B45309)', color: '#1A1204' },
        // Variante fantôme de la charte : transparente, bordée de verre. Seule
        // la couleur par défaut (primary) devient fantôme ; les boutons
        // contournés colorés (avertissement, erreur) gardent leur couleur, qui
        // porte leur sens.
        outlined: { borderRadius: 14 },
        outlinedPrimary: {
          borderColor: GLASS_BORDER,
          color: INK,
          '&:hover': { borderColor: SKY, backgroundColor: GLASS },
        },
        textPrimary: { color: SKY },
      },
    },
    MuiIconButton: { styleOverrides: { root: { '&:hover': { backgroundColor: GLASS } } } },
    MuiCircularProgress: { styleOverrides: { colorPrimary: { color: SKY } } },

    // Champs : fond bleuté, bordure claire, texte blanc, marque de saisie à 45 %.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: FIELD_BG,
          borderRadius: 12,
          color: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(126,179,255,.45)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY_LIGHT, borderWidth: 1 },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(59,130,246,.05)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(126,179,255,.14)' },
          },
        },
        input: {
          color: '#fff',
          '&::placeholder': { color: 'rgba(255,255,255,.45)', opacity: 1 },
          '&.Mui-disabled': { WebkitTextFillColor: FAINT },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: SLATE, '&.Mui-focused': { color: SKY }, '&.Mui-disabled': { color: FAINT } },
      },
    },
    MuiFormLabel: { styleOverrides: { root: { color: SLATE, '&.Mui-focused': { color: SKY } } } },
    MuiFormHelperText: { styleOverrides: { root: { color: FAINT } } },
    MuiSelect: { styleOverrides: { icon: { color: SLATE } } },
    MuiInputAdornment: { styleOverrides: { root: { color: SLATE } } },
    MuiFormControlLabel: { styleOverrides: { label: { color: INK } } },

    // Un menu flotte au-dessus du contenu : il doit être opaque, sinon on lit
    // le tableau au travers de ses options. Même règle que le panneau de
    // notifications.
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: PANEL,
          backgroundImage: 'none',
          border: `1px solid ${GLASS_BORDER}`,
          borderRadius: 14,
          backdropFilter: 'none',
          boxShadow: '0 12px 32px rgba(4,9,20,.55)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          '&:hover': { backgroundColor: GLASS },
          '&.Mui-selected': { backgroundColor: 'rgba(59,130,246,.18)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(59,130,246,.24)' },
        },
      },
    },
    MuiPopover: { styleOverrides: { paper: { backgroundImage: 'none' } } },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${ROW_LINE}`, fontSize: 14, color: INK },
        head: {
          fontFamily: HEAD,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.5px',
          textTransform: 'uppercase',
          color: FAINT,
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${GLASS_BORDER}`,
        },
      },
    },
    // La charte veut un en-tête de tableau sans fond, souligné d'un filet. Le
    // sélecteur descendant (`.MuiTableHead-root .MuiTableRow-root`, deux
    // classes) l'emporte sur une couleur posée par `sx` sur la ligne.
    MuiTableHead: {
      styleOverrides: {
        root: { '& .MuiTableRow-root': { backgroundColor: 'transparent' } },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: HOVER },
          '&:last-child td': { borderBottom: 'none' },
        },
      },
    },
    // Rien sur MuiTableContainer : des écrans l'utilisent en
    // `component={Paper}`, et un fond ici entrerait en conflit avec le verre du
    // Paper.

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 700, fontSize: 12, height: 24 },
        label: { paddingLeft: 9, paddingRight: 9 },
        // Une pastille sans couleur (un prospect, un brouillon) n'est pas une
        // pastille grise : c'est la teinte bleue neutre de la charte.
        filledDefault: { backgroundColor: 'rgba(59,130,246,.18)', color: SLATE },
        filledPrimary: { backgroundColor: 'rgba(59,130,246,.22)', color: SKY },
        filledSuccess: pastille('success'),
        filledWarning: pastille('warning'),
        filledError: pastille('error'),
        filledInfo: pastille('info'),
        outlinedDefault: { borderColor: GLASS_BORDER, color: SLATE },
        sizeSmall: { height: 22, fontSize: 11.5 },
      },
    },

    // Alert est un Paper : sans ces remises à zéro, il hériterait du verre,
    // du rayon 22 et de l'ombre bleue des cartes, et on ne distinguerait plus
    // un avertissement d'un panneau ordinaire.
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backdropFilter: 'none',
          boxShadow: 'none',
          fontSize: 14,
          alignItems: 'flex-start',
        },
        standardSuccess: notice('success'),
        standardWarning: notice('warning'),
        standardError: notice('error'),
        standardInfo: {
          color: INK,
          backgroundColor: FIELD_BG,
          border: `1px solid ${GLASS_BORDER}`,
          '& .MuiAlert-icon': { color: SKY },
        },
        outlinedSuccess: notice('success'),
        outlinedWarning: notice('warning'),
        outlinedError: notice('error'),
        outlinedInfo: { color: INK, border: `1px solid ${GLASS_BORDER}`, '& .MuiAlert-icon': { color: SKY } },
        action: { paddingTop: 2 },
      },
    },
    MuiAlertTitle: { styleOverrides: { root: { fontFamily: HEAD, fontWeight: 700, color: 'inherit' } } },

    // Le verre du Paper suffit à la fenêtre elle-même ; ce qu'il faut ajouter,
    // c'est le voile sombre derrière, sans lequel une fenêtre translucide se
    // confond avec la page qu'elle recouvre.
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: 'rgba(11,23,48,.92)', backdropFilter: 'blur(18px)', backgroundImage: 'none' },
      },
    },
    MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(4,9,20,.72)' } } },
    MuiDialogTitle: { styleOverrides: { root: { fontFamily: HEAD, fontWeight: 700, fontSize: 19, color: INK } } },
    MuiDialogContentText: { styleOverrides: { root: { color: SLATE } } },

    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${PANEL_LINE}`, minHeight: 44 },
        indicator: { backgroundColor: SKY, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: HEAD,
          fontWeight: 600,
          textTransform: 'none',
          color: SLATE,
          minHeight: 44,
          '&.Mui-selected': { color: INK },
        },
      },
    },

    // Laissé à lui-même en mode sombre, le Stepper rend des ronds gris très
    // sombres et un connecteur presque invisible : l'assistant d'ouverture
    // d'établissement n'indiquerait plus où l'on en est.
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(126,179,255,.20)',
          '&.Mui-active': { color: PRIMARY_LIGHT },
          '&.Mui-completed': { color: SUCCESS },
        },
        text: { fill: INK, fontFamily: HEAD, fontWeight: 600 },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: SLATE,
          fontFamily: HEAD,
          '&.Mui-active': { color: INK, fontWeight: 600 },
          '&.Mui-completed': { color: SLATE },
        },
      },
    },
    MuiStepConnector: { styleOverrides: { line: { borderColor: 'rgba(126,179,255,.20)' } } },

    MuiDivider: { styleOverrides: { root: { borderColor: PANEL_LINE } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: PANEL,
          border: `1px solid ${GLASS_BORDER}`,
          color: INK,
          fontFamily: BODY,
          fontSize: 12.5,
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(4,9,20,.55)',
        },
        arrow: { color: PANEL },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: 'rgba(126,179,255,.30)', opacity: 1 },
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY_LIGHT, opacity: 1 },
        },
      },
    },
    MuiCheckbox: { styleOverrides: { root: { color: SLATE, '&.Mui-checked': { color: PRIMARY_LIGHT } } } },
    MuiLink: { styleOverrides: { root: { color: SKY, textDecorationColor: 'rgba(126,179,255,.4)' } } },
    MuiSnackbarContent: {
      styleOverrides: {
        root: { backgroundColor: PANEL, backgroundImage: 'none', color: INK, borderRadius: 14 },
      },
    },
  },
});

export default theme;
